import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import { Accordion, Button, Nav } from "react-bootstrap";
import { FaSignOutAlt, FaTimes, FaFileInvoice, FaReceipt, FaList, FaClipboard, FaUser, FaGavel, FaClock, FaUsers, FaFileAlt, FaDollarSign, FaHome, FaCalendarCheck, FaChartLine, FaUserCircle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../assets/styles/index.css";
import { logout } from "../slices/authSlice";

const Sidebar = ({ isOpen, onClose }) => {
  const { userInfo } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);
  const username = userInfo && userInfo[2]?.InfoField;
  const empCode = userInfo && userInfo[1]?.InfoField;
  const userType = userInfo && userInfo[1]?.InfoField2;

  const logoutHandler = async () => {
    try {
      dispatch(logout());
      navigate("/login");
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Define all sidebar routes
  const mainOptions = [
    { key: 'dashboard', path: '/dashboard' },
    { key: 'profile', path: '/profile' },
    { key: 'quickinfo', path: '/quick-info-dashboard' },
    { key: 'userOptions', children: ['/', '/yearly'] },
    { key: 'adminOptions', children: [
      '/salary-slip', '/salary-sheet-summary', '/bonus-form-c', '/form-24',
      '/employee-personal-file-data', '/bonus-monthly', '/gratuity-report',
      '/form-25', '/attendance-register-form-12', '/employee-full-final',
      '/form-21', '/leave-register-form-14-15', '/overtime-register',
      '/form-22', '/attendance'] }
  ];

  let activeSection = null;
  let activeChild = null;
  for (const option of mainOptions) {
    if (option.path && location.pathname === option.path) {
      activeSection = option.key;
      break;
    }
    if (option.children) {
      for (const child of option.children) {
        if (location.pathname === child) {
          activeSection = option.key;
          activeChild = child;
          break;
        }
      }
    }
    if (activeSection) break;
  }

  return (
    <>
      <div 
        className={`sidebar-overlay ${isOpen ? 'active' : ''}`} 
        onClick={onClose} 
        ref={overlayRef} 
      />
    <div ref={sidebarRef} className={`sidebar ${isOpen ? "open" : ""}`}>
      {isOpen && (
        <Button onClick={onClose} className="sidebar-close">
          <FaTimes className="white-icon" />
        </Button>
      )}

      {userInfo ? (
        <>
            <div className="sidebar-header-container">
          <Nav.Link as={Link} to="/profile" className="no-style-navlink">
                <div className="sidebar-header">
              <div className="image-container">
                <FaUserCircle className="sidebar-profile-icon" />
              </div>
                  <div className="sidebar-username">
                    {username && <div className="sidebar-name">{username + " (" + empCode + ")"}</div>}
                  </div>
                </div>
              </Nav.Link>
            </div>
            <div className="sidebar-menu-container">
          <Nav className="flex-column">
            {/* Unified sidebar list: Dashboard, Profile, Quick Info Dashboard, User Options, Admin Options */}
            <Nav.Link
              as={Link}
              to="/dashboard"
              className={classNames("sidebar-link", {
                "active-link": activeSection === 'dashboard',
              })}
            >
              <FaHome className="me-2" /> Dashboard
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/profile"
              className={classNames("sidebar-link", {
                "active-link": activeSection === 'profile',
              })}
            >
              <FaUser className="me-2" /> Profile
            </Nav.Link>
            {(userType === "3" || userType === 3) && (
              <Nav.Link
                as={Link}
                to="/quick-info-dashboard"
                className={classNames("sidebar-link", {
                  "active-link": activeSection === 'quickinfo',
                })}
              >
                <FaChartLine className="me-2" /> Quick Info Dashboard
              </Nav.Link>
            )}
            {/* User Options Accordion */}
            <Accordion defaultActiveKey={activeSection === 'userOptions' ? "2" : undefined} className="sidebar-accordion">
              <Accordion.Item eventKey="2">
                <Accordion.Header className={activeSection === 'userOptions' ? "active-link" : ""}>User Options</Accordion.Header>
                <Accordion.Body>
                  <Nav.Link
                    as={Link}
                    to="/"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'userOptions' && activeChild === '/',
                    })}
                  >
                    Salary Slip
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/yearly"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'userOptions' && activeChild === '/yearly',
                    })}
                  >
                    Yearly Leave
                  </Nav.Link>
                </Accordion.Body>
              </Accordion.Item>
              {/* Admin Options Accordion */}
              <Accordion.Item eventKey="3">
                <Accordion.Header className={activeSection === 'adminOptions' ? "active-link" : ""}>Admin Options</Accordion.Header>
                <Accordion.Body className="admin-options-body">
                  <Nav.Link
                    as={Link}
                    to="/salary-slip"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/salary-slip',
                    })}
                  >
                    <FaFileInvoice className="me-2" /> Salary Slip
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/salary-sheet-summary"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/salary-sheet-summary',
                    })}
                  >
                    <FaReceipt className="me-2" /> Salary Sheet and Summary
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/bonus-form-c"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/bonus-form-c',
                    })}
                  >
                    <FaList className="me-2" /> Bonus Form C
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/form-24"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/form-24',
                    })}
                  >
                    <FaClipboard className="me-2" /> Form 24 (Contract Act)
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/employee-personal-file-data"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/employee-personal-file-data',
                    })}
                  >
                    <FaUser className="me-2" /> Employee Personal File Data
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/bonus-monthly"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/bonus-monthly',
                    })}
                  >
                    <FaList className="me-2" /> Bonus Monthly Summary
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/gratuity-report"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/gratuity-report',
                    })}
                  >
                    <FaGavel className="me-2" /> Gratuity Report
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/form-25"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/form-25',
                    })}
                  >
                    <FaClipboard className="me-2" /> Form 25 (Contract Act)
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/attendance-register-form-12"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/attendance-register-form-12',
                    })}
                  >
                    <FaClock className="me-2" /> Attendance Register (Form-12)
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/employee-full-final"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/employee-full-final',
                    })}
                  >
                    <FaUsers className="me-2" /> Employee Full and Final
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/form-21"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/form-21',
                    })}
                  >
                    <FaClipboard className="me-2" /> Form 21 (Factory Act)
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/leave-register-form-14-15"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/leave-register-form-14-15',
                    })}
                  >
                    <FaFileAlt className="me-2" /> Leave Register Form No 14 & 15
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/overtime-register"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/overtime-register',
                    })}
                  >
                    <FaDollarSign className="me-2" /> Overtime Register
                  </Nav.Link>



                  <Nav.Link
                    as={Link}
                    to="/form-22"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/form-22',
                    })}
                  >
                    <FaClipboard className="me-2" /> Form 22 (Factory Act)
                  </Nav.Link>



                  <Nav.Link
                    as={Link}
                    to="/pfmonthwisesummary"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/pfmonthwisesummary',
                    })}
                  >
                    <FaClipboard className="me-2" /> PF Monthwise Summary
                  </Nav.Link>



                  <Nav.Link
                    as={Link}
                    to="/yearlymandaysreport"
                    className={classNames("sidebar-link", {
                      "active-link": activeSection === 'adminOptions' && activeChild === '/yearlymandaysreport',
                    })}
                  >
                    <FaClipboard className="me-2" /> Yearly Man Days Report
                  </Nav.Link>

                  {/* Removed Attendance option here */}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Nav>
            </div>

            <div className="sidebar-footer-container">
            <Button onClick={logoutHandler} className="sidebar-logout">
              <FaSignOutAlt className="white-icon" /> Logout
            </Button>
          </div>
        </>
      ) : (
          <div className="sidebar-footer-container">
          <Button onClick={onClose} className="sidebar-logout">
            <FaSignOutAlt className="white-icon" /> Login
          </Button>
        </div>
      )}
    </div>
    </>
  );
};

export default Sidebar;
